
import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import {
  Hidden,
  Grid,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@material-ui/core';
import Slider from '@ant-design/react-slick';
import '../../static/css/style.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Helmet } from 'react-helmet';

// custom components
import NavBar from '../nav/NavBar';
import Footer from '../footer/Footer';
import FooterSpacer from '../footer/FooterSpacer';

// photos
import FindCallingHero from '../../static/img/teach_page/find_your_calling_hero.png';
import SquatingDancer from '../../static/img/teach_page/squating_dancer.png';
import TeachingRightForYouHero from '../../static/img/teach_page/teaching_right_for_you_hero.png';
import Step1 from '../../static/img/teach_page/step1.png';
import Step2 from '../../static/img/teach_page/step2.png';

// icons
import WhiteBelt from '../../static/img/icons/class_type_icons/white_belt_icon.svg';
import GreenBelt from '../../static/img/icons/class_type_icons/green_belt_icon.svg';
import BlueBelt from '../../static/img/icons/class_type_icons/blue_belt_icon.svg';
import BrownBelt from '../../static/img/icons/class_type_icons/brown_belt_icon.svg';
import BlackBelt from '../../static/img/icons/class_type_icons/black_belt_icon.svg';
import FirstDegree from '../../static/img/icons/class_type_icons/first_degree_icon.svg';
import SecondDegree from '../../static/img/icons/class_type_icons/second_degree_icon.svg';
import Moving2Heal from '../../static/img/icons/class_type_icons/moving_to_heal_icon.svg';
import MoveIt from '../../static/img/icons/class_type_icons/move_it_icon.svg';
import FreeDance from '../../static/img/icons/class_type_icons/freedance_icon.svg';
import FiveStages from '../../static/img/icons/class_type_icons/five_stages_icon.svg';
import FloorPlay from '../../static/img/icons/class_type_icons/floorplay_icon.svg';

import checkmark from '../../static/img/icons/checked_icon.svg';

// testimonial data
import TestimonialData from '../../static/testimonial_data_teach_page';

// Convert to Array and split for 2 sliders
const testimonialArray = Object.values(TestimonialData);
const half = Math.ceil(testimonialArray.length / 2);
const firstSliderContent = testimonialArray.splice(0, half);
const secondSliderContent = testimonialArray.splice(0, half);

const text = {
  becomeTeacher: 'Become a Nia Teacher',
  to: 'To . . .',
  inspirePeople: 'Become a leader and share the joy of Nia with your community',
  empowerOthers: 'Empower others to become the healthiest, strongest, most authentic and alive versions of themselves',
  earnMoney: 'Earn money doing what you love',
  theNiaTeaching: 'The Nia Teaching',
  path: 'Path . . .',
  thePathOfTeaching: 'The path of teaching Nia calls to individuals from all different walks of life.',
  longTimeTeacher: 'Whether you are a long-time Nia student who wants to step-in to teaching, or you are new-to-Nia and already feeling the call to share this work in a professional capacity, the path of becoming a Nia teacher is for you.',
  step1: 'step 1',
  step2: 'step 2',
  completeNiaWhite: 'Complete the Nia White Belt Training',
  becomeSacredMember: 'Become a Sacred Livelihood Member',
  niaWhiteBelt: 'The Nia White Belt - the Art of Sensation - is the first step on your journey to become a Nia teacher. In this training, you will Learn + Embody:',
  niaMindful: 'Nia\'s Mindful Practices',
  foudationalNia: '13 Foundational Nia White Belt Principles',
  fiftyTwoMoves: 'Nia\'s 52 Moves',
  primarilyTaken: 'The personal growth, awareness and development you\'ll discover in the Nia White Belt forms the foundation for you to step-in to leadership as a Nia teacher through continued education.',
  sacredLivelihood: 'Your Sacred Livelihood Membership is your all-access pass to begin sharing Nia in a professional capacity. Your membership includes:',
  thirteenLesson: '13-Lesson Self-Guided "SHARE" online curriculum that will teach you to:',
  somaticAwareness: 'How to apply the somatic awareness you gained in white belt in a teaching capacity',
  learnToEmbody: 'How to learn and embody your first Nia routine',
  toolsAndTips: 'Tools and tips on how to effectively deliver the Nia class experience',
  teachingLicense: 'Nia Teaching License',
  educationTraining: 'Education, training and support to apply your white belt knowledge as a Nia teacher',
  accessToTools: 'Tools, templates and support to build and market your Nia teaching business',
  wonderingTeaching: 'Wondering if Teaching is Right for You?',
  forMany: 'For many, teaching Nia becomes a life-long passion offering ongoing personal and professional growth and development.',
  forThosWhoChoose: 'For those who choose to continue the path of Nia mastery, there are many opportunities for continuing education with Nia programs…',
  whiteBelt: 'White Belt:',
  greenBelt: 'Green Belt:',
  blueBelt: 'Blue Belt:',
  brownBelt: 'Brown Belt:',
  blackBelt: 'Black Belt:',
  firstDegree: 'First Degree:',
  secondDegree: 'Second Degree:',
  whiteBDesc: 'Sensation',
  greenBDesc: 'Teach',
  blueBDesc: 'Communicate',
  brownBDesc: 'Perceive',
  blackBDesc: 'Create',
  firstDDesc: 'Self',
  secondDDesc: 'Alchemy',
  move2Heal: 'Moving to Heal:',
  moveIt: 'Move It:',
  freeDance: 'FreeDance:',
  fiveStages: '5 Stages:',
  floorPlay: 'Floorplay:',
  move2HealDesc: 'Art of Feeling Better',
  moveItDesc: 'Art of Movement',
  freeDanceDesc: 'Art of Movement Creativity',
  fiveStagesDesc: 'Art of Self-Healing',
  floorPlayDesc: 'Art of Play',
  findANia: 'Find a Nia White Belt Training',
  learnAbout: 'Learn About Membership',
  scheduleACall: 'Schedule a Call with an Advisor Today',
  findATraining: 'Find a Training',
  niaBodyLife: 'Nia Body + Life Trainings',
  niaSpecialty: 'Nia Specialty Trainings',
};

const bodyLifeTrainings = {
  whitebelt: {
    icon: WhiteBelt,
    belt: text.whiteBelt,
    goal: text.whiteBDesc,
  },
  greenBelt: {
    icon: GreenBelt,
    belt: text.greenBelt,
    goal: text.greenBDesc,
  },
  blueBelt: {
    icon: BlueBelt,
    belt: text.blueBelt,
    goal: text.blueBDesc,
  },
  brownBelt: {
    icon: BrownBelt,
    belt: text.brownBelt,
    goal: text.brownBDesc,
  },
  blackBelt: {
    icon: BlackBelt,
    belt: text.blackBelt,
    goal: text.blackBDesc,
  },
  firstDegree: {
    icon: FirstDegree,
    belt: text.firstDegree,
    goal: text.firstDDesc,
  },
  secondDegree: {
    icon: SecondDegree,
    belt: text.secondDegree,
    goal: text.secondDDesc,
  },
};

const specialtyTrainings = {
  moving2Heal: {
    icon: Moving2Heal,
    specialty: text.move2Heal,
    art: text.move2HealDesc,
  },
  moveIt: {
    icon: MoveIt,
    specialty: text.moveIt,
    art: text.moveItDesc,
  },
  freeDance: {
    icon: FreeDance,
    specialty: text.freeDance,
    art: text.freeDanceDesc,
  },
  fiveStages: {
    icon: FiveStages,
    specialty: text.fiveStages,
    art: text.fiveStagesDesc,
  },
  floorPlay: {
    icon: FloorPlay,
    specialty: text.floorPlay,
    art: text.floorPlayDesc,
  },
};

const styles = theme => ({
  appBarSpacer: theme.mixins.toolbar,
  page: {
    position: 'relative',
    width: '100%',
    minHeight: '100vh',
    backgroundColor: 'white',
  },
  container: {
    width: 'auto',
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.up(800 + theme.spacing(2 * 2))]: {
      width: 800,
      marginLeft: 'auto',
      marginRight: 'auto',
    },
  },
  hero: {
    height: 'auto',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  content: {
    lineHeight: 1.8,
  },
  popRight: {
    marginLeft: theme.spacing(2),
  },
  popRighter: {
    marginLeft: theme.spacing(3),
  },
  centerText: {
    textAlign: 'center',
    marginBottom: theme.spacing(2),
  },
  centerItem: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
  },
  standardizedButton: {
    width: '80%',
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  standardizedButtonPadTop: {
    width: '80%',
    marginTop: theme.spacing(2),
    textAlign: 'center',
    [theme.breakpoints.up(600)]: {
      marginTop: '72px',
    },
  },
  button: {
    textAlign: 'center',
  },
  h3: {
    fontSize: '1.5rem',
    // letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  paddingRight: {
    color: 'red',
  },
  section1Header: {
    fontSize: '2.125em',
    textTransform: 'uppercase',
    letterSpacing: '5px',
  },
  spaceTopBottom: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  quoteCred: {
    textAlign: 'right',
    fontWeight: 'bold',
  },
  slider: {
    width: '100%',
    marginBottom: theme.spacing(8),
    marginTop: theme.spacing(6),
    padding: theme.spacing(),
  },
  testimonialPortrait: {
    width: '100%',
    height: 'auto',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down(600)]: {
      width: '70%',
    },
  },
  icon: {
    height: '20px',
    width: 'auto',
    marginRight: theme.spacing(),
  },
  horizontalFlex: {
    textTransform: 'uppercase',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  verticalFlex: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-around',
  },
  spaceAbove: {
    marginTop: theme.spacing(4),
  },
  trainingType: {
    marginRight: theme.spacing(0.5),
  },
  space: {
    marginTop: theme.spacing(),
    marginBottom: theme.spacing(),
  },
  dabbingHero: {
    width: '100%',
    height: 'auto',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: theme.spacing(0.85),
  },
  popOutLeft: {
    fontSize: '2.125em',
    textTransform: 'uppercase',
    [theme.breakpoints.up(800 + theme.spacing(2 * 2))]: {
      marginLeft: theme.spacing(-3),
    },
  },
  popDown: {
    paddingTop: theme.spacing(8),
  },
  greenText: {
    color: '#547948',
  },
  flex: {
    display: 'flex',
    flexDirection: 'column',
  },
  bottomGrid: {
    marginBottom: theme.spacing(4),
  },
  findTrainingButton: {
    textAlign: 'center',
    [theme.breakpoints.down(600)]: {
      width: '80%',
      marginBottom: theme.spacing(2),
    },
  },
  testimonialCard: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  trainings: {
    [theme.breakpoints.down(600)]: {
      paddingLeft: theme.spacing(4),
      paddingRight: theme.spacing(4),
    },
  },
  checkmark: {
    height: 28,
    width: 28,
  },
});

let TestimonialCard = ({
  classes, portrait, quote, quotee, alt,
}) => (
    // eslint-disable-next-line react/jsx-indent
    <Grid className={classes.testimonialCard} container spacing={4}>
      <Grid item xs={12} sm={4}>
        <img className={classes.testimonialPortrait} alt={alt} src={portrait} />
      </Grid>
      <Grid item xs={12} sm={8} className={classes.verticalFlex}>
        <div className={classes.verticalFlex}>
          <Typography variant="body1" color="textSecondary" className={classes.content}>{quote}</Typography>
          <br />
          <Typography variant="body1" color="textSecondary" className={classes.quoteCred}>{quotee}</Typography>
        </div>
      </Grid>
    </Grid>
    // eslint-disable-next-line indent
  );

TestimonialCard.propTypes = {
  classes: PropTypes.object.isRequired,
  portrait: PropTypes.string.isRequired,
  quote: PropTypes.string.isRequired,
  quotee: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
};

TestimonialCard = withStyles(styles)(TestimonialCard);

let BodyLifeTraining = ({ classes, training }) => (
  <div className={classes.space}>
    <div className={classes.horizontalFlex}>
      <img className={classes.icon} alt="Icon for related training type" src={training.icon} />
      <Typography variant="body1" className={classes.trainingType}><strong>{training.belt}</strong></Typography>
      <Typography variant="body1" className={classes.trainingDescription}>{training.goal}</Typography>
    </div>
  </div>
);

BodyLifeTraining.propTypes = {
  classes: PropTypes.object.isRequired,
  training: PropTypes.object.isRequired,
};

BodyLifeTraining = withStyles(styles)(BodyLifeTraining);

let SpecialtyTraining = ({ classes, training }) => (
  <div>
    <div className={classes.horizontalFlex}>
      <img className={classes.icon} alt="Icon for related training type" src={training.icon} />
      <Typography variant="body1" className={classes.trainingType}><strong>{training.specialty}</strong></Typography>
    </div>
    <div className={classes.popRighter}>
      <Typography variant="body1" className={classes.trainingDescription}>{training.art}</Typography>
    </div>
  </div>
);

SpecialtyTraining.propTypes = {
  classes: PropTypes.object.isRequired,
  training: PropTypes.object.isRequired,
};

SpecialtyTraining = withStyles(styles)(SpecialtyTraining);

const TeachPage = (props) => {
  const { classes } = props;

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 10000,
  };

  return (
    <div className={classes.page}>

      <Helmet>
        <title>Nia Teachers | Become a Nia Instructor Today</title>
        <meta name="description" content="Nia's instructors are the community leaders that help us share the magic of Nia. Become a Nia instructor and earn money doing what you love." />
        <meta name="keywords" content="dance, fitness, classes" />
      </Helmet>

      <NavBar />
      <main className={classes.container}>
        <div className={classes.appBarSpacer} />
        <img className={classes.hero} alt="Nia Teacher" src={FindCallingHero} />
        <Grid className={classes.spaceTopBottom} container>
          <Grid className={classes.paddingRight} item xs={12} sm={5}>
            <Typography variant="h1" color="textSecondary" className={classes.section1Header}>{text.becomeTeacher}</Typography>
            <Typography variant="h1" color="textSecondary" className={classes.section1Header}>{text.to}</Typography>
          </Grid>
          <hr />
          <Grid item xs={12} sm={5}>
            <List className={classes.list}>
              <ListItem className={classes.listItem}>
                <ListItemIcon><img src={checkmark} alt="checkmark" className={classes.checkmark} /></ListItemIcon>
                <ListItemText>{text.inspirePeople}</ListItemText>
              </ListItem>
              <ListItem className={classes.listItem}>
                <ListItemIcon><img src={checkmark} alt="checkmark" className={classes.checkmark} /></ListItemIcon>
                <ListItemText>{text.empowerOthers}</ListItemText>
              </ListItem>
              <ListItem className={classes.listItem}>
                <ListItemIcon><img src={checkmark} alt="checkmark" className={classes.checkmark} /></ListItemIcon>
                <ListItemText>{text.earnMoney}</ListItemText>
              </ListItem>
            </List>
          </Grid>
        </Grid>
        <Slider className={classes.slider} {...sliderSettings}>
          {firstSliderContent.map(testimonial => (
            <TestimonialCard
              portrait={testimonial.photo}
              quote={testimonial.quote}
              quotee={testimonial.name}
              key={testimonial.name}
              alt={testimonial.alt}
            />
          ))}
        </Slider>
        {/* TODO: add an invisible title here that becomes visible in mobile */}
        <Grid className={classes.teachingPath} container spacing={4}>
          <Grid item xs={12} sm={6}>
            <img className={classes.dabbingHero} alt="Squating Dancer - Nia" src={SquatingDancer} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <div className={classes.popDown}>
              <Typography color="textSecondary" variant="h2" className={classes.popOutLeft}>{text.theNiaTeaching}</Typography>
              <br />
              <Typography color="textSecondary" variant="h2" className={classes.popOutLeft}>{text.path}</Typography>
              <div className={classes.popDown}>
                <br />
                <Typography variant="body1" className={classes.greenText}>{text.thePathOfTeaching}</Typography>
                <br />
                <Typography variant="body1" className={classes.content}>{text.longTimeTeacher}</Typography>
              </div>
            </div>
          </Grid>
        </Grid>
        <Grid spacing={4} container>
          <Grid className={classes.flex} item xs={12} sm={6}>
            <img className={classes.hero} alt="The Nia White Belt Training" src={Step1} />
            <div className={classes.popRight}>
              <div className={classes.centerText}>
                <Typography variant="h5" color="textSecondary" className={classes.h3}>{text.completeNiaWhite}</Typography>
              </div>
              <Typography variant="body1" className={classes.content}>{text.niaWhiteBelt}</Typography>
              <ul>
                <li><Typography variant="body1" className={classes.content}>{text.niaMindful}</Typography></li>
                <li><Typography variant="body1" className={classes.content}>{text.foudationalNia}</Typography></li>
                <li><Typography variant="body1" className={classes.content}>{text.fiftyTwoMoves}</Typography></li>
              </ul>
              <Typography variant="body1" className={classes.content}>{text.primarilyTaken}</Typography>
            </div>
            <div className={classes.centerItem}>
              <Button variant="outlined" className={classes.standardizedButton} href="/finder/training">{text.findANia}</Button>
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <img className={classes.hero} alt="Sacred Livelihood Membership - Nia" src={Step2} />
            <div className={classes.popRight}>
              <div className={classes.centerText}>
                <Typography variant="h5" color="textSecondary" className={classes.h3}>{text.becomeSacredMember}</Typography>
              </div>
              <Typography variant="body1" className={classes.content}>{text.sacredLivelihood}</Typography>
              <ul>
                <li><Typography variant="body1" className={classes.content}>{text.educationTraining}</Typography></li>
                <li><Typography variant="body1" className={classes.content}>{text.teachingLicense}</Typography></li>
                <li><Typography variant="body1" className={classes.content}>{text.accessToTools}</Typography></li>
              </ul>
            </div>
            <div className={classes.centerItem}>
              <Button variant="outlined" className={classes.standardizedButtonPadTop} href="/membership">{text.learnAbout}</Button>
            </div>
          </Grid>
        </Grid>
        <Slider className={classes.slider} {...sliderSettings}>
          {secondSliderContent.map(testimonial => (
            <TestimonialCard
              portrait={testimonial.photo}
              quote={testimonial.quote}
              quotee={testimonial.name}
              key={testimonial.name}
              alt={testimonial.alt}
            />
          ))}
        </Slider>
        <a href="mailto: jessica@nianow.com?subject=I'M INTERESTED IN BECOMING A NIA TEACHER">
          <img className={classes.hero} alt="Nia Dance Classes" src={TeachingRightForYouHero} />
        </a>
        <Grid container className={classes.bottomGrid}>
          <Grid item xs={12} sm={3}>
            <Hidden smUp>
              <div className={classes.centerItem}>
                <Button variant="outlined" className={classes.findTrainingButton} href="/finder/training">{text.findATraining}</Button>
              </div>
            </Hidden>
            <Typography variant="body1" className={classes.content}>{text.forMany}</Typography>
            <br />
            <Typography variant="body1" className={classes.content}>{text.forThosWhoChoose}</Typography>
            <br />
            <Hidden xsDown>
              <Button variant="outlined" className={classes.findTrainingButton} href="/finder/training">{text.findATraining}</Button>
            </Hidden>
          </Grid>
          <hr />
          <Grid className={classes.trainings} item xs={12} sm={8}>
            <Grid spacing={4} container>
              <Grid item xs={12} sm={6}>
                <Button variant="outlined" className={classes.button} href="/trainings">{text.niaBodyLife}</Button>
                <div className={classes.spaceAbove}>
                  {Object.keys(bodyLifeTrainings).map(value => (
                    <BodyLifeTraining training={bodyLifeTrainings[value]} key={bodyLifeTrainings[value].belt} />
                  ))}
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button variant="outlined" className={classes.button} href="/specialtytrainings">{text.niaSpecialty}</Button>
                <div className={classes.spaceAbove}>
                  {Object.keys(specialtyTrainings).map(value => (
                    <SpecialtyTraining training={specialtyTrainings[value]} key={specialtyTrainings[value].specialty} />
                  ))}
                </div>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <FooterSpacer />
      </main>
      <Footer />
    </div>
  );
};

TeachPage.propTypes = {
  classes: PropTypes.object.isRequired,

};

export default withStyles(styles)(TeachPage);
